import { createTrackingConfig } from '../../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../../config/appConfig';
import CustomApprovalTemplatesSplitView from './CustomApprovalTemplatesSplitView';
import ApprovalTemplateForm from './components/ApprovalTemplateForm';

export const approvalTemplatesConfig = {
  entityName: 'Approval Template',
  
  // Entity information for file attachments and audit logs
  entityTypeId: 11, // Approval Templates entity type ID (adjust as needed)
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/approval-templates',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`
    },
    lookupEndpoints: {
      users: '/users',
      departments: '/department',
      entityTypes: '/entity-type',
      priorities: '/priority'
    },
    // SWR invalidation endpoints
    swrInvalidation: {
      mainList: '/approval-templates'
    }
  },

  // UI Configuration
  ui: {
    drawerWidth: '30%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default',
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
    showViewButton: true,
    showEditButton: true,
    showDeleteButton: true,
    showActionsColumn: true,
    showAddButton: true
  },

  // Features configuration
  features: {
    fileAttachments: false,
    masterEntityTypeId: 11,
    approvalTemplates: true
  },

  // Custom Split View Panel configuration
  customSplitViewPanel: {
    enabled: true,
    component: CustomApprovalTemplatesSplitView
  },

  // Custom Form Panel configuration
  customFormPanel: {
    enabled: true,
    // Use ApprovalTemplateForm for both add and edit
    component: ApprovalTemplateForm,
    addComponent: ApprovalTemplateForm,
    editComponent: ApprovalTemplateForm
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'name',
      label: 'Template Name',
      type: 'text',
      required: true,
      placeholder: 'Enter template name',
      table: { header: 'Name', width: '250px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        minLength: 3,
        maxLength: 255
      }
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter template description',
      rows: 3,
      table: false,
      filterable: false
    },
    // Workflow Type
    {
      name: 'workflowType',
      label: 'Workflow Type',
      type: 'select',
      required: true,
      options: [
        { value: 'review', label: 'Review Only' },
        { value: 'approval', label: 'Approval Only' },
        { value: 'review_approval', label: 'Review & Approval' }
      ],
      table: {
        header: 'Workflow',
        width: '120px',
        sortable: true,
        cellRenderer: 'workflowTypeBadge'
      },
      filterable: true,
      filterType: 'multiSelect'
    },
   
    // Entity Type
    {
      name: 'entityType',
      label: 'Entity Type',
      type: 'select',
      lookupEndpoint: 'entityTypes',
      options: [],
      table: {
        header: 'Entity Type',
        width: '150px',
        sortable: true,
        cellRenderer: 'entityTypeBadge',
        displayField: 'entityType' // Show entityType relation
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Priority
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      lookupEndpoint: 'priorities',
      options: [],
      table: {
        header: 'Priority',
        width: '100px',
        sortable: true,
        cellRenderer: 'priorityBadge',
        displayField: 'priority' // Show priority relation
      },
      filterable: true,
      filterType: 'multiSelect'
    },
    // Approval Type
    {
      name: 'approvalType',
      label: 'Approval Type',
      type: 'select',
      required: true,
      options: [
        { value: 'single', label: 'Single' },
        { value: 'sequential', label: 'Sequential' },
        { value: 'parallel', label: 'Parallel' },
        { value: 'any_one', label: 'Any One' },
        { value: 'review_approval', label: 'Review & Approval' }
      ],
      table: {
        header: 'Approval Type',
        width: '120px',
        sortable: true,
        cellRenderer: 'approvalTypeBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true
      }
    },
    // Is Active
    {
      name: 'isActive',
      label: 'Active',
      type: 'checkbox',
      table: {
        header: 'Active',
        width: '80px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      filterType: 'boolean'
    },
    // Steps (computed/display only)
    {
      name: 'steps',
      label: 'Steps',
      type: 'computed',
      table: {
        header: 'Steps',
        width: '100px',
        sortable: false,
        render: (value) => {
          // Ensure value is an array before accessing length
          if (!value) {
            return <span className="text-sm text-gray-500">No steps</span>;
          }
          if (!Array.isArray(value)) {
            return <span className="text-sm text-gray-500">Invalid steps</span>;
          }
          if (value.length === 0) {
            return <span className="text-sm text-gray-500">No steps</span>;
          }
          return <span className="text-sm font-medium">{value.length} step{value.length !== 1 ? 's' : ''}</span>;
        }
      },
      filterable: false
    },
    // Created At
    {
      name: 'createdAt',
      label: 'Created At',
      type: 'date',
      readonly: true,
      table: {
        header: 'Created',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    // Created By
    {
      name: 'createdBy',
      label: 'Created By',
      type: 'computed',
      table: {
        header: 'Created By',
        width: '150px',
        sortable: true,
        cellRenderer: 'userDisplay'
      },
      filterable: true,
      filterType: 'text'
    }
  ],

  // COMPUTED CONFIGURATIONS
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        render: field.table.render,
        originalField: field.name
      }));
  },

  exportFileName: 'approval_templates',

  // Custom handlers
  customHandlers: {
    onSave: null,
    onDelete: null,
    onLoad: null,
    onFormOpen: null
  },

  // Data transformation
  transformData: (data) => {
    const transformed = { ...data };

    // Numeric fields
    const intFields = ['entityTypeId', 'priorityId', 'createdById', 'updatedById'];
    intFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const n = parseInt(transformed[field], 10);
          if (!Number.isNaN(n)) transformed[field] = n;
          else delete transformed[field];
        }
      }
    });

    // Boolean fields
    if (transformed.isActive !== undefined) {
      transformed.isActive = Boolean(transformed.isActive);
    }

    // Handle steps array if present
    if (transformed.steps && Array.isArray(transformed.steps)) {
      transformed.steps = transformed.steps.map(step => ({
        ...step,
        stepNumber: parseInt(step.stepNumber, 10),
        approverId: step.approverId ? parseInt(step.approverId, 10) : null,
        escalationDays: step.escalationDays ? parseInt(step.escalationDays, 10) : 7
      }));
    }

    // JSON fields
    if (transformed.autoAssignRules && typeof transformed.autoAssignRules === 'string') {
      try {
        transformed.autoAssignRules = JSON.parse(transformed.autoAssignRules);
      } catch (e) {
        transformed.autoAssignRules = {};
      }
    }

    return transformed;
  },

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  // Filter Groups
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name', 'entityType', 'approvalType', 'workflowType', 'isActive']
    },
    {
      name: 'advanced',
      label: 'Advanced',
      icon: '⚙️',
      fields: ['priority', 'createdAt', 'createdBy']
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    approvalType: ['single', 'sequential', 'parallel', 'any_one', 'review_approval'],
    workflowType: ['review', 'approval', 'review_approval'],
    priority: ['low', 'medium', 'high', 'urgent']
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Notification messages
  messages: {
    loading: 'Loading approval templates...',
    noData: 'No approval templates found. Create your first template to get started.',
    createSuccess: 'Approval template created successfully!',
    updateSuccess: 'Approval template updated successfully!',
    deleteSuccess: 'Approval template deleted successfully!',
    createError: 'Failed to create approval template',
    updateError: 'Failed to update approval template',
    deleteError: 'Failed to delete approval template',
    loadError: 'Failed to load approval templates',
    validationError: 'Validation failed'
  },

  // Form Layout Configuration
  formSections: [
    {
      title: 'Basic Information',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['name', 'description'],
      fieldSpans: {
        'name': 'md:col-span-12',
        'description': 'md:col-span-12'
      }
    },
    {
      title: 'Template Configuration',
      icon: '⚙️',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['entityType', 'priority', 'approvalType', 'workflowType', 'isActive'],
      fieldSpans: {
        'entityType': 'md:col-span-3',
        'priority': 'md:col-span-3',
        'approvalType': 'md:col-span-3',
        'workflowType': 'md:col-span-3',
        'isActive': 'md:col-span-12'
      }
    }
  ],

  // Tracking Section
  trackingSection: createTrackingConfig('Approval Templates'),

  // Labels
  labels: {},

  // Help Text
  helpText: {
    name: { short: 'Name of the approval template.' },
    description: { short: 'Description of what this template is used for.' },
    entityType: { short: 'Type of entity this template applies to.' },
    priority: { short: 'Default priority level for requests using this template.' },
    approvalType: { short: 'Approval workflow type (Single/Sequential/Parallel/Any One/Review & Approval).' },
    workflowType: { short: 'Workflow process type (Review Only/Approval Only/Review & Approval).' },
    isActive: { short: 'Whether this template is currently active and available for use.' }
  },

  // Fields to remove during data preparation
  fieldsToRemove: [
    'id', 'createdAt', 'updatedAt', 'createdById', 'updatedById',
    'createdBy', 'updatedBy',
    'entityType', 'priority',
    'steps' // Steps are handled separately in the form
  ],

  validateField: null
};

